/**
 * Scheduler-local session gate (separate from Portal2 cookies).
 *
 * Portal cookies often remain after "logout" in this app — we must not re-apply them
 * until the user returns via a fresh portal handoff (?handoff=1).
 */

const LOGGED_OUT_KEY = 'gantt-planner:loggedOut';
const SESSION_ACTIVE_KEY = 'gantt-planner:schedulerSessionActive';

export function isSchedulerLoggedOut(): boolean {
  if (typeof localStorage === 'undefined') {
    return false;
  }
  try {
    return localStorage.getItem(LOGGED_OUT_KEY) === '1';
  } catch {
    return false;
  }
}

export function isSchedulerSessionActive(): boolean {
  if (typeof localStorage === 'undefined') {
    return false;
  }
  try {
    return localStorage.getItem(SESSION_ACTIVE_KEY) === '1';
  } catch {
    return false;
  }
}

/** True when Portal2 linked the user here with an explicit handoff query param. */
export function isPortalHandoff(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const params = new URLSearchParams(window.location.search);
  const handoff = params.get('handoff');
  return handoff === '1' || handoff === 'true';
}

/** Portal cookies may be applied (handoff or active scheduler session, not logged out). */
export function canApplyPortalSession(): boolean {
  if (isPortalHandoff()) {
    return true;
  }
  if (isSchedulerLoggedOut()) {
    return false;
  }
  return true;
}

export function markSchedulerSessionActive(): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  try {
    localStorage.setItem(SESSION_ACTIVE_KEY, '1');
    localStorage.removeItem(LOGGED_OUT_KEY);
  } catch {
    // ignore
  }
}

export function markSchedulerLoggedOut(): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  try {
    localStorage.setItem(LOGGED_OUT_KEY, '1');
    localStorage.removeItem(SESSION_ACTIVE_KEY);
  } catch {
    // ignore
  }
}

export function clearSchedulerLoggedOutFlag(): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  try {
    localStorage.removeItem(LOGGED_OUT_KEY);
  } catch {
    // ignore
  }
}

/** Remove ?handoff= / ?projectId= handoff params from the address bar after a successful portal entry. */
export function stripPortalHandoffFromUrl(): void {
  if (typeof window === 'undefined') {
    return;
  }
  const url = new URL(window.location.href);
  let changed = false;
  for (const key of ['handoff', 'projectId'] as const) {
    if (url.searchParams.has(key)) {
      url.searchParams.delete(key);
      changed = true;
    }
  }
  if (!changed) {
    return;
  }
  const next = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState(window.history.state, '', next);
}
